/**
 * BetaApplicationTypes.ts
 * Type definitions for Beta Application form fields
 *
 * Provides standardized types and enums for all beta application fields,
 * ensuring consistent data structure across the application wizard.
 */

/**
 * Primary trading focus for beta applicants
 */
export type TradingFocus =
  | 'futures'     // Futures contracts
  | 'forex'       // Forex/CFD trading
  | 'crypto'      // Cryptocurrency
  | 'stocks'      // Stocks and ETFs
  | 'options'    // Options contracts
  | 'other';    // Other assets

/**
 * Trading focus constants for consistent usage
 */
export const TradingFocusEnum = {
  Futures: 'futures',
  Forex: 'forex',
  Crypto: 'crypto',
  Stocks: 'stocks',
  Options: 'options',
  Other: 'other'
} as const;

/**
 * Trading experience levels
 */
export type ExperienceLevel =
  | 'less-than-6-months'    // Less than 6 months
  | '6-months-to-2-years'   // 6 months - 2 years
  | '2-to-5-years'          // 2-5 years
  | '5-plus-years';         // 2-5 years

/**
 * Experience level constants
 */
export const ExperienceLevelEnum = {
  LessThanSixMonths: 'less-than-6-months',
  SixMonthsToTwoYears: '6-months-to-2-years',
  TwoToFiveYears: '2-to-5-years',
  FivePlusYears: '5-plus-years'
} as const;

/**
 * Primary trading styles
 */
export type TradingStyle =
  | 'scalper'     // Scalper (seconds to minutes)
  | 'day-trader'  // Day Trader (minutes to hours)
  | 'swing-trader'  // Swing Trader (days to weeks)
  | 'positional-trader'  // Positional Trader / Investor (weeks, months or years)
  | 'other';      // Other (user specified)

/**
 * Trading style constants
 */
export const TradingStyleEnum = {
  Scalper: 'scalper',
  DayTrader: 'day-trader',
  SwingTrader: 'swing-trader',
  PositionalTrader: 'positional-trader',
  Other: 'other'
} as const;

/**
 * Beta application account types
 */
export type BetaAccountType =
  | 'prop-firm'   // Prop firm account (funded or evaluation)
  | 'personal'   // Personal trading account
  | 'paper'   // Paper trading account
  | 'retirement'   // Retirement trading account
  | 'business'   // Business trading account
  | 'trust'   // Trust trading account
  | 'pooled'   // Pooled trading account
  | 'other';   // Other trading account

/**
 * Beta account type constants
 */
export const BetaAccountTypeEnum = {
  PropFirm: 'prop-firm',
  Personal: 'personal',
  Paper: 'paper',
  Retirement: 'retirement',
  Business: 'business',
  Trust: 'trust',
  Pooled: 'pooled',
  Other: 'other'
} as const;

/**
 * Primary trading sessions
 */
export type TradingSessions =
  | 'asian'    // Asian Session
  | 'london'   // London Session
  | 'ny-am'    // NY AM Session
  | 'ny-pm'   // NY PM Session
  | 'other';   // Other - user specified

/**
 * Trading sessions constants
 */
export const TradingSessionsEnum = {
  Asian: 'asian',
  London: 'london',
  NewYorkAM: 'ny-am',
  NewYorkPM: 'ny-pm',
  Other: 'other'
} as const;

/**
 * Beta testing commitment levels
 */
export type TestingCommitment =
  | 'yes-committed'         // Yes, I can commit to regular testing
  | 'maybe-limited'         // Maybe, depending on my schedule
  | 'no-limited';          // No, I have limited availability

/**
 * Testing commitment constants
 */
export const TestingCommitmentEnum = {
  YesCommitted: 'yes-committed',
  MaybeLimited: 'maybe-limited',
  NoLimited: 'no-limited'
} as const;

/**
 * Feedback participation levels
 */
export type FeedbackCommitment =
  | 'yes-committed'         // Yes, I'm committed to providing regular feedback
  | 'yes-limited'           // Yes, but my feedback may be limited
  | 'no-minimal';          // No, I prefer minimal feedback requirements

/**
 * Feedback commitment constants
 */
export const FeedbackCommitmentEnum = {
  YesCommitted: 'yes-committed',
  YesLimited: 'yes-limited',
  NoMinimal: 'no-minimal'
} as const;

/**
 * Beta application wizard steps
 */
export type BetaApplicationStep =
  | 'account'       // Account Information
  | 'trading'       // Trading Setup
  | 'style-experience'  // Style & Experience
  | 'commitment'    // Beta Commitment
  | 'review';       // Application Review

/**
 * Application step constants
 */
export const BetaApplicationStepEnum = {
  Account: 'account',
  Trading: 'trading',
  StyleExperience: 'style-experience',
  Commitment: 'commitment',
  Review: 'review'
} as const;

/**
 * Prop firm status types
 */
export type PropFirmStatus =
  | 'evaluation'   // In evaluation phase
  | 'funded';      // Funded trader

/**
 * Prop firm status constants
 */
export const PropFirmStatusEnum = {
  Evaluation: 'evaluation',
  Funded: 'funded'
} as const;

/**
 * Prop firm payout history types
 */
export type PropFirmPayoutHistory =
  | 'yes-received'        // Yes, I have received payouts
  | 'no-none'            // No, I have not received payouts
  | 'prefer-not-to-say'; // I prefer not to say

/**
 * Prop firm payout history constants
 */
export const PropFirmPayoutHistoryEnum = {
  YesReceived: 'yes-received',
  NoNone: 'no-none',
  PreferNotToSay: 'prefer-not-to-say'
} as const;